import {
  Box,
  Button,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  IconButton,
  List,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { memo, useCallback, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import type { EmailDraftV2 } from 'apis/userStorage';
import { appActions } from 'app/app.actions';
import { TopbarButton } from 'app/views/layout/components/layoutContainer/components/topBar/components/button/topbarButton';
import { AppLink } from 'components/appLink';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
// eslint-disable-next-line no-restricted-imports
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import styles from './emailTopbarButton.module.scss';

const numberOfVisibleDrafts = 5;

export const EmailTopbarButton = memo(
  function EmailTopbarButton(): JSX.Element {
    const dispatch = useAppDispatch();
    const { data: userStorage, set: setUserStorage } = useServerStorage('user');

    const [isOpen, setIsOpen] = useState(false);

    // TODO: Replace composeEmailGetAllFrom with integrationsApi.endpoints.readEmailIntegrations
    // (RTK Query, already exists). The data currently lands in state.composeEmail.fromEmails which
    // ConversationForm reads for its "No email integration found" warning. To migrate:
    // 1. Use useQuery(readEmailIntegrations) here or in ConversationFormHookAdapter
    // 2. Pass the result as a prop to ConversationForm, removing its dependency on the Redux slice
    // 3. Delete the fromEmails/getAllFrom state from the composeEmail reducer
    useMount(() => {
      dispatch(composeEmailActions.composeEmailGetAllFrom());
    });

    const allDrafts = useMemo(
      () => userStorage.drafts?.email ?? [],
      [userStorage.drafts?.email],
    );

    const visibleDrafts = useMemo(
      (): EmailDraftV2[] =>
        allDrafts.slice(-numberOfVisibleDrafts).reverse() as EmailDraftV2[],
      [allDrafts],
    );

    const notVisibleDraftsNumber = allDrafts.length - visibleDrafts.length;

    const handleToggle = useCallback((isOpen = true) => {
      setIsOpen(isOpen);
      if (isOpen) ampli.topbarEmailOpened();
    }, []);

    const handleOpenCompose = useCallback(
      (draft?: EmailDraftV2) => {
        dispatch(
          appActions.showModal({
            modal: {
              type: 'app:composeEmail',
              arg: draft ? { draft } : {},
            },
          }),
        );
      },
      [dispatch],
    );

    const handleRemoveDraft = useCallback(
      (draft: EmailDraftV2) => {
        setUserStorage({
          drafts: (storage) => {
            if (!storage.email) return;
            const idx = storage.email.findIndex(
              (d) => d.timestamp === draft.timestamp,
            );
            if (idx !== -1) storage.email.splice(idx, 1);
          },
        });
      },
      [setUserStorage],
    );

    const label = 'New email';

    const tooltip = (
      <CommandKeybindingsTooltipContent commandName="conversation:composeNewEmail">
        {label}
      </CommandKeybindingsTooltipContent>
    );

    return (
      <Popover.Root open={isOpen} onOpenChange={handleToggle}>
        <Popover.Trigger asChild>
          <TopbarButton
            icon="mail"
            label={label}
            tooltip={tooltip}
            notification={allDrafts.length > 0 ? allDrafts.length : undefined}
            data-testid="compose-email-button"
          />
        </Popover.Trigger>

        <Popover.Content
          className={styles.popover}
          side="bottom"
          align="start"
          sideOffset={parseInt(sizes.spacer.values[100])}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Columns>
            <Column className={styles.content}>
              <Column.Row slottable borderBottom>
                <Column.Slot placement="start">
                  <Text.Heading>Email</Text.Heading>
                </Column.Slot>

                <Column.Slot placement="end">
                  <Tooltip content={tooltip}>
                    <Button
                      type="button"
                      variant="secondary"
                      style={{ width: '100%' }}
                      onClick={() => handleOpenCompose()}
                    >
                      Compose
                    </Button>
                  </Tooltip>
                </Column.Slot>
              </Column.Row>

              <Column.Row
                scrollable
                noHorizontalPadding
                noVerticalPadding={visibleDrafts.length > 0}
              >
                {visibleDrafts.length ? (
                  <Box padding="200 0">
                    <List className={styles.list}>
                      {visibleDrafts.map((draft, index) => (
                        <List.Item
                          key={index}
                          withHoverEffect={false}
                          title={
                            <FlexBox flexDirection="column" padding="0 0 0 250">
                              <Text.Paragraph
                                variant="2"
                                as="em"
                                color="secondary"
                              >
                                {draft.subject || 'Unknown'} (Unsent draft)
                              </Text.Paragraph>

                              <Text.Paragraph
                                as="div"
                                variant="4"
                                color="tertiary"
                              >
                                {moment(draft.timestamp).fromNow()}
                              </Text.Paragraph>
                            </FlexBox>
                          }
                          contentRight={
                            <FlexBox>
                              <Tooltip content="Edit">
                                <IconButton
                                  icon="pen"
                                  variant="tertiary"
                                  onClick={() => handleOpenCompose(draft)}
                                />
                              </Tooltip>

                              <Tooltip content="Delete">
                                <IconButton
                                  icon="trash"
                                  variant="tertiary"
                                  onClick={() => handleRemoveDraft(draft)}
                                />
                              </Tooltip>
                            </FlexBox>
                          }
                        />
                      ))}
                    </List>
                  </Box>
                ) : (
                  <EmptyState
                    title="No email drafts"
                    description="You haven't saved any email drafts yet."
                    icon={null}
                  />
                )}
              </Column.Row>

              <Column.Row slottable noMinHeight borderTop>
                <Column.Slot placement="center">
                  <AppLink path="/drafts" onClick={() => handleToggle(false)}>
                    {notVisibleDraftsNumber > 0
                      ? `See all drafts (${notVisibleDraftsNumber} more)`
                      : 'See all drafts'}
                  </AppLink>
                </Column.Slot>
              </Column.Row>
            </Column>
          </Columns>
        </Popover.Content>
      </Popover.Root>
    );
  },
);
